/* global Office */

/**
 * OfficeContext - Office.js initialization and host bridge provider
 *
 * Wraps the taskpane route to:
 *  1. Call Office.onReady() and track initialization state
 *  2. Detect the Office host (PowerPoint or Excel) and expose the matching
 *     bridge service (PowerPointBridge or ExcelBridge) to child components
 *  3. Detect whether the app is running inside Office
 *
 * The same hosted bundle loads in both PowerPoint and Excel; we branch on
 * Office.HostType at runtime. Consumers read `app` ('powerpoint' | 'excel')
 * and the generic `bridge` (the active host bridge). The legacy `powerpoint`
 * field is kept for back-compat.
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { PowerPointBridge } from './PowerPointBridge';
import { ExcelBridge } from './ExcelBridge';
import { WordBridge } from './WordBridge';

const OfficeContext = createContext(null);

export function OfficeProvider({ children }) {
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isInOffice, setIsInOffice] = useState(false);
  const [error] = useState(null);
  const bridgeRef = useRef(null);
  const appRef = useRef(null); // 'powerpoint' | 'excel' | null

  useEffect(() => {
    // Check if Office.js is available on the global scope
    if (typeof Office === 'undefined' || !Office.onReady) {
      console.warn('[OfficeContext] Office.js not available – running outside Office host');
      setIsInOffice(false);
      setIsOfficeReady(true); // Mark ready so children render (web fallback)
      return;
    }

    let resolved = false;

    // Timeout fallback — if Office.onReady doesn't fire within 5s, proceed without Office
    const timer = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.warn('[OfficeContext] Office.onReady timed out – running in browser fallback');
        setIsInOffice(false);
        setIsOfficeReady(true);
      }
    }, 5000);

    Office.onReady((info) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      console.log('[OfficeContext] Office.onReady', info);

      // Detect platform for Office Online serialization (shared by both hosts)
      const platform = info.platform || Office.context?.platform;
      const isOnline = platform === Office.PlatformType?.OfficeOnline ||
        platform === 'OfficeOnline' || String(platform).includes('Online');

      if (info.host === Office.HostType.PowerPoint) {
        setIsInOffice(true);
        appRef.current = 'powerpoint';
        bridgeRef.current = new PowerPointBridge({ isOfficeOnline: isOnline });
      } else if (info.host === Office.HostType.Excel) {
        setIsInOffice(true);
        appRef.current = 'excel';
        bridgeRef.current = new ExcelBridge({ isOfficeOnline: isOnline });
      } else if (info.host === Office.HostType.Word) {
        setIsInOffice(true);
        appRef.current = 'word';
        bridgeRef.current = new WordBridge({ isOfficeOnline: isOnline });
      } else {
        console.warn('[OfficeContext] Unsupported Office host:', info.host);
        setIsInOffice(false);
      }

      setIsOfficeReady(true);
    });

    return () => clearTimeout(timer);
  }, []);

  const app = appRef.current; // 'powerpoint' | 'excel' | 'word' | null
  const value = {
    isOfficeReady,
    isInOffice,
    app,
    bridge: bridgeRef.current,                                  // active host bridge
    powerpoint: app === 'powerpoint' ? bridgeRef.current : null, // back-compat
    excel: app === 'excel' ? bridgeRef.current : null,
    word: app === 'word' ? bridgeRef.current : null,
    error,
  };

  return (
    <OfficeContext.Provider value={value}>
      {children}
    </OfficeContext.Provider>
  );
}

/**
 * Hook to access Office.js context
 * @returns {{ isOfficeReady: boolean, isInOffice: boolean, app: ('powerpoint'|'excel'|'word'|null), bridge: (PowerPointBridge|ExcelBridge|WordBridge|null), powerpoint: PowerPointBridge | null, excel: ExcelBridge | null, word: WordBridge | null, error: Error | null }}
 */
export function useOffice() {
  const ctx = useContext(OfficeContext);
  if (!ctx) {
    throw new Error('useOffice must be used within an OfficeProvider');
  }
  return ctx;
}
